import type { ChangeEvent } from 'react';
import type { IUserTableFilters } from 'src/types/user';
import type { UseSetStateReturn } from 'minimal-shared/hooks';

import { useBoolean, usePopover } from 'minimal-shared/hooks';
import React, { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Link,
  Button,
  Dialog,
  Tooltip,
  useTheme,
  Typography,
  InputLabel,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { CustomPopover } from 'src/components/custom-popover';

import TableAdvanceFilter from './table-advanceFilter-custom';

// ----------------------------------------------------------------------

type Props = {
  onResetPage: () => void;
  filters: UseSetStateReturn<IUserTableFilters>;
  options: {
    roles: string[];
  };
  actionButtonLabel: string;
  actionButtonHandler?: () => void;
  isToolbarVisible: boolean;
  isActionButton?: Array<'import' | 'export'>;
  actionhref?: string;
  isShowCreateBtn?: boolean;
  isSearchFullWidth?: boolean;
  onFileSelect?: (data: { file: File; content: string }) => void;
  onExportContact?: () => void;
  currentModule?: string;
  moduleName?: string;
  isCreate: boolean;
  isImport: boolean;
  isExport: boolean;
  advanceFilterFields?: any;
  setAdvanceFilter?: any;
  isAdvanceFilter?: boolean;
  advanceFilters?: any;
  refreshPage?: () => void;
  refreshing?: boolean;
  resetPagination?: any;
  isDisableSync?: boolean;
  isSearchInput?: boolean;
  selectedfilters?: any;
  handleRemoveAllFilter?: () => void;
  dateTimeFiltre?: any;
  isPrivacyPolicyUrls?: {
    message: string;
    links: { label: string; url: string }[];
  } | null;
};

// Custom Debounce Hook
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function TableToolbar({
  filters,
  options,
  onResetPage,
  actionButtonLabel,
  actionButtonHandler = () => {},
  isToolbarVisible = true,
  isActionButton = [],
  actionhref = '',
  isShowCreateBtn = true,
  isSearchFullWidth = false,
  onFileSelect,
  onExportContact,
  currentModule = 'Unknown',
  moduleName = '',
  isCreate = false,
  isImport = false,
  isExport = false,
  advanceFilterFields = [],
  setAdvanceFilter = () => {},
  isAdvanceFilter = false,
  advanceFilters,
  refreshPage = undefined,
  refreshing = false,
  resetPagination = () => {},
  isPrivacyPolicyUrls = null,
  isDisableSync = false,
  isSearchInput = true,
  selectedfilters = [],
  handleRemoveAllFilter = () => {},
  dateTimeFiltre = [],
}: Props) {
  const menuActions = usePopover();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslate('translation');
  const theme = useTheme();
  const importModal = useBoolean();

  const { state: currentFilters, setState: updateFilters } = filters;
  const [searchTerm, setSearchTerm] = useState(currentFilters.name || '');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);

  useEffect(() => {
    if (debouncedSearchTerm !== currentFilters.name) {
      onResetPage();
      updateFilters({ name: debouncedSearchTerm });
    }
  }, [currentFilters.name, debouncedSearchTerm, onResetPage, updateFilters]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleImportClick = () => importModal.onTrue();

  const handleFileChange = (file: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const fileContent = reader.result;
      if (fileContent && typeof fileContent === 'string') {
        onFileSelect?.({ file, content: fileContent });
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    reader.readAsText(file);
  };

  const handleExport = () => {
    menuActions.onClose();
    onExportContact?.();
  };

  const renderMenuActions = () => (
    <CustomPopover
      open={menuActions.open}
      anchorEl={menuActions.anchorEl}
      onClose={menuActions.onClose}
      slotProps={{ arrow: { placement: 'right-top' } }}
    >
      <MenuList>
        {isActionButton.includes('import') && isImport && (
          <MenuItem
            onClick={() => {
              handleImportClick();
              menuActions.onClose();
            }}
          >
            <Iconify icon="solar:import-bold" />
            {t('IMPORT')}
          </MenuItem>
        )}
        {isActionButton.includes('export') && isExport && (
          <MenuItem onClick={handleExport}>
            <Iconify icon="solar:export-bold" />
            {t('EXPORT')}
          </MenuItem>
        )}
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          width: '100%',
        }}
      >
        {/* Left Section: Create Button + Policy */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          {isShowCreateBtn && isCreate && (
            <Button
              component={RouterLink}
              href={actionhref}
              variant="contained"
              startIcon={
                <Iconify
                  icon="mingcute:add-line"
                  sx={{
                    color:
                      theme.palette.mode === 'dark'
                        ? theme.palette.common.black
                        : theme.palette.common.white,
                  }}
                />
              }
              onClick={actionButtonHandler}
              sx={{
                background: '#50a28a',
                whiteSpace: 'nowrap',
                paddingX: 2,
                paddingY: 1,
              }}
              disabled={isDisableSync}
            >
              {actionButtonLabel}
            </Button>
          )}

          {isPrivacyPolicyUrls && isPrivacyPolicyUrls?.links?.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                ml: { xs: 0, sm: 2 },
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {isPrivacyPolicyUrls.message ?? 'Please review the following policies:'}
              </Typography>
              {isPrivacyPolicyUrls.links.map((link, index) => (
                <Link
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {link.label}
                  {index < isPrivacyPolicyUrls.links.length - 1 && <span>&nbsp;|&nbsp;</span>}
                </Link>
              ))}
            </Box>
          )}
        </Box>

        {/* Right Section: Filters, Search, Actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1,
            flexGrow: 1,
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            width: '100%',
          }}
        >
          {/* Clear Filters */}
          {((selectedfilters && Object.keys(selectedfilters).length > 0) ||
            (dateTimeFiltre && dateTimeFiltre.length > 0)) && (
            <Button
              size="small"
              color="error"
              onClick={handleRemoveAllFilter}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Iconify icon="solar:trash-bin-trash-bold" sx={{ mr: 0.5 }} />
              {t('CLEAR_FILTERS')}
            </Button>
          )}

          {/* Search Input */}
          {isToolbarVisible && isSearchInput && (
            <TextField
              size="small"
              sx={{
                width: { xs: '100%', sm: isSearchFullWidth ? '100%' : 200 },
                minWidth: 120,
              }}
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={`${t('SEARCH')}...`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {/* Refresh Button */}
          {refreshPage && (
            <Tooltip title={t('REFRESH')}>
              <IconButton onClick={refreshPage} color="primary" disabled={refreshing}>
                <Iconify icon="solar:restart-bold" />
              </IconButton>
            </Tooltip>
          )}

          {/* Advance Filter */}
          {isAdvanceFilter && (
            <TableAdvanceFilter
              advanceFilterFields={advanceFilterFields}
              setAdvanceFilter={setAdvanceFilter}
              advanceFilters={advanceFilters}
              resetPagination={resetPagination}
            />
          )}

          {/* Action Menu */}
          {isActionButton?.length > 0 && (isExport || isImport) && (
            <IconButton onClick={menuActions.onOpen}>
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>
          )}
        </Box>
      </Box>

      <ImportContactsDialog
        t={t}
        open={importModal.value}
        onClose={() => importModal.onFalse()}
        onImport={handleFileChange}
        currentModule={currentModule}
      />

      {renderMenuActions()}
    </>
  );
}

interface ImportContactsDialogProps {
  t: any;
  open: boolean;
  onClose: () => void;
  onImport?: (file: File) => void;
  currentModule: string;
}

const ImportContactsDialog: React.FC<ImportContactsDialogProps> = ({
  t,
  open,
  onClose,
  onImport,
  currentModule,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<boolean>(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv') {
        setFileError(true);
        setFile(null);
        return;
      }
      setFileError(false);
      setFile(selectedFile);
    }
  };

  const handleImport = () => {
    if (file) {
      if (onImport) onImport(file);
      onClose();
    }
  };

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileError(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('IMPORT_MODULE', { module: t(currentModule.toUpperCase()) })}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" mb={2}>
          {t('IMPORT_CSV_DESCRIPTION', { module: t(currentModule.toUpperCase()) })} {'  '}
          <Link href={`/assets/${currentModule}.csv`} underline="hover" download>
            {t('DOWNLOAD_CSV')}
          </Link>
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <InputLabel htmlFor="csv-upload" sx={{ fontWeight: 500 }}>
            {t('CSV_FILE')}
          </InputLabel>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <label htmlFor="csv-upload" style={{ cursor: 'pointer' }}>
            <IconButton component="span" color="primary">
              <Iconify icon="mdi:file-import" />
            </IconButton>
            <Typography variant="body2" component="span">
              {file ? file.name : t('CHOOSE_FILE')}
            </Typography>
          </label>
        </Box>
        {fileError && (
          <Typography variant="body2" my={0.5} color="error">
            {t('ONLY_CSV_FILES_ARE_ALLOWED')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '15px' }}>
        <Button onClick={onClose} variant="outlined">
          {t('CANCEL')}
        </Button>
        <Button onClick={handleImport} variant="contained" disabled={!file || fileError}>
          {t('IMPORT')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface PolicyLink {
  label: string;
  url: string;
}

interface PolicyLinksNoticeProps {
  links: PolicyLink[];
  message?: string;
}

const PolicyLinksNotice: React.FC<PolicyLinksNoticeProps> = ({ links, message }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
    <Typography variant="body2">
      {message ?? 'Please review the following policies:'}&nbsp; &nbsp;
      {links.map((link, index) => (
        <React.Fragment key={link.label}>
          <Link href={link.url} target="_blank" rel="noopener noreferrer" underline="hover">
            {link.label}
          </Link>
          {index < links.length - 1 && <span>&nbsp;|&nbsp;</span>}
        </React.Fragment>
      ))}
    </Typography>
  </Box>
);
